import { useTheme } from '@emotion/react';
import type { FC } from 'react';

import AlertTriangleIcon from '@/assets/svg/ui-elements/alert-triangle.svg';
import CheckIcon from '@/assets/svg/ui-elements/check-outline.svg';
import ChevronDownIcon from '@/assets/svg/ui-elements/chevron-down.svg';
import ChevronRightIcon from '@/assets/svg/ui-elements/chevron-right.svg';
import LockIcon from '@/assets/svg/ui-elements/lock.svg';

import Typography from '../Typography';
import { deriveState } from './derive-state';
import {
  DoneIcon,
  NESTED_INDICATOR_SIZE,
  NestedRoot,
  NestedStepList,
  NestedStepRow,
  nestedTone,
  SectionIcon,
  SectionItem,
  SectionList,
  SectionRow,
  StepIndicator,
  StepLabel,
} from './Stepper.styled';
import type { NestedStepperProps, StepperNestedStepState } from './types';

const ORDER: {
  before: StepperNestedStepState;
  at: StepperNestedStepState;
  after: StepperNestedStepState;
} = { before: 'done', at: 'pressed', after: 'inactive' };

export const NestedStepper: FC<NestedStepperProps> = ({
  sections,
  value,
  onStepClick,
  onSectionClick,
  ...rest
}) => {
  const theme = useTheme();
  const flatKeys = sections.flatMap((section) => section.steps.map((step) => step.key));
  const selectedIndex = flatKeys.indexOf(value ?? '');
  const stepInteractive = Boolean(onStepClick);

  return (
    <NestedRoot {...rest}>
      <SectionList>
        {sections.map((section) => {
          const sectionState = section.state ?? 'expanded';
          const locked = sectionState === 'locked';
          const sectionInteractive = Boolean(onSectionClick) && !locked;

          return (
            <SectionItem key={section.key}>
              <SectionRow
                as={onSectionClick ? 'button' : 'div'}
                type={onSectionClick ? 'button' : undefined}
                disabled={onSectionClick && locked ? true : undefined}
                aria-expanded={sectionInteractive ? sectionState === 'expanded' : undefined}
                $locked={locked}
                $interactive={sectionInteractive}
                onClick={sectionInteractive ? () => onSectionClick?.(section.key) : undefined}
              >
                <StepLabel $noWrap={false}>
                  <Typography as="span" variant="body2" fontWeight={500} color="inherit">
                    {section.label}
                  </Typography>
                </StepLabel>

                {sectionState === 'alert' && (
                  <SectionIcon $tone="warning" data-testid="stepper-alert-icon">
                    <AlertTriangleIcon aria-hidden="true" />
                  </SectionIcon>
                )}

                <SectionIcon $tone={locked ? 'disabled' : 'secondary'}>
                  {locked ? (
                    <LockIcon aria-hidden="true" />
                  ) : sectionState === 'expanded' ? (
                    <ChevronDownIcon aria-hidden="true" />
                  ) : (
                    <ChevronRightIcon aria-hidden="true" />
                  )}
                </SectionIcon>
              </SectionRow>

              {sectionState === 'expanded' && (
                <NestedStepList>
                  {section.steps.map((step, index) => {
                    const flatIndex = flatKeys.indexOf(step.key);
                    const selected = flatIndex === selectedIndex;
                    const state = deriveState(step.state, flatIndex, selectedIndex, ORDER);
                    const tone = nestedTone(theme, state);

                    return (
                      <li key={step.key}>
                        <NestedStepRow
                          as={stepInteractive ? 'button' : 'div'}
                          type={stepInteractive ? 'button' : undefined}
                          $interactive={stepInteractive}
                          aria-current={selected ? 'step' : undefined}
                          onClick={stepInteractive ? () => onStepClick?.(step.key) : undefined}
                        >
                          {state === 'done' ? (
                            <DoneIcon>
                              <CheckIcon aria-hidden="true" />
                            </DoneIcon>
                          ) : (
                            <StepIndicator
                              data-part="indicator"
                              $size={NESTED_INDICATOR_SIZE}
                              $fill={tone.fill}
                              $onFill={tone.onFill}
                            >
                              <Typography as="span" variant="body3" color="inherit">
                                {index + 1}
                              </Typography>
                            </StepIndicator>
                          )}

                          <StepLabel $noWrap={false}>
                            <Typography
                              as="span"
                              variant="body2"
                              color={
                                state === 'done'
                                  ? theme.colors.text.secondary
                                  : theme.colors.text.primary
                              }
                            >
                              {step.label}
                            </Typography>
                          </StepLabel>
                        </NestedStepRow>
                      </li>
                    );
                  })}
                </NestedStepList>
              )}
            </SectionItem>
          );
        })}
      </SectionList>
    </NestedRoot>
  );
};
